import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NotFoundScreen } from '@/common/ErrorScreen';
import { TitleWithQuestion } from '@/common/TitleWithQuestion';
import { FollowUpRequestBoardDetailModal } from '@/follow-up/FollowUpRequestBoardDetailModal';
import FollowUpRequestBoardTable from '@/tables/FollowUpRequestBoardTable';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { PageContainer } from '~/styles/boardStyles';

export const Route = createFileRoute('/_app/follow-up-request-board/')({
  component: FollowUpRequestBoardScreen,
});

/**
 * The network-wide Follow-up Request Board.
 *
 * Coordinators claim work here: approved requests no coordinator has claimed
 * and that haven't become follow-ups, defaulted to their own countries and
 * student languages. Taking one writes the coordinator claim onto the request,
 * which grants access to that student.
 *
 * Legacy's page and its data endpoint had no gate at all; the
 * coordinator-or-admin check it declared is enforced here.
 */
function FollowUpRequestBoardScreen() {
  const { t } = useTranslation();
  const { me, isPending } = useCurrentUser();
  const [detailRequestId, setDetailRequestId] = useState<number | null>(null);

  // Gated on `profile_type`: `useCurrentUser().isStudyHelper` also includes
  // study helpers and partners, which the endpoint refuses, and
  // `useIsCoordinator` needs a group slug, so on a top-level route it never
  // resolves.
  //
  // `isPending` matters: `/users/me` is pending on first paint, and a bare
  // `!canView` bounces every caller. Defence in depth; the list, the detail and
  // Take each refuse on their own.
  const canView =
    me?.profile_type === 'FOLLOWUP_COORDINATOR' || me?.profile_type === 'ADMIN';

  if (isPending) return null;
  if (!canView) return <NotFoundScreen />;

  return (
    <PageContainer>
      <div style={{ margin: '9px 0 29px 0' }}>
        <TitleWithQuestion
          title={t('wbs.follow-up-request-board')}
          placement="follow_up_request_board"
        />
      </div>
      <FollowUpRequestBoardTable onViewDetail={setDetailRequestId} />
      <FollowUpRequestBoardDetailModal
        requestId={detailRequestId}
        onClose={() => setDetailRequestId(null)}
      />
    </PageContainer>
  );
}
